import api from "./axios";

export type DashboardPeriod =
  "today" | "7_days" | "30_days" | "6_months" | "1_year" | "all_time";

export type DashboardTrend = "UP" | "DOWN" | "FLAT" | "NEW" | "UNAVAILABLE";

export type DashboardChange = {
  percentage: string | null;
  trend: DashboardTrend;
};

export type DashboardDirectionMetric = {
  count: number;
  previousCount: number | null;
  change: DashboardChange;
  volumeNgn: string | null;
  previousVolumeNgn: string | null;
  volumeChange: DashboardChange;
  volumeCoverage: {
    includedTransactions: number;
    excludedTransactions: number;
  };
};

export type DashboardRate = {
  provider: "quidax" | "treasura";
  providerRate: string | null;
  customerRate: string | null;
  marginType: "FLAT" | "PERCENTAGE";
  marginValue: string;
  asOf: string | null;
};

export type DashboardSummary = {
  period: {
    key: DashboardPeriod;
    start: string | null;
    end: string;
    previousStart: string | null;
    previousEnd: string | null;
    timezone: "UTC";
  };
  transactions: {
    onRamp: DashboardDirectionMetric;
    offRamp: DashboardDirectionMetric;
  };
  rates: {
    onRamp: DashboardRate;
    offRamp: DashboardRate;
  };
  pricingSpread: {
    estimatedOnRampNgn: string | null;
    offRampConfiguredNgn: string | null;
    offRampRetainedBeforeProviderFeesNgn: string | null;
    estimatedTotalNgn: string | null;
    coverage: {
      onRamp: DashboardCoverage;
      offRamp: DashboardCoverage;
    };
  };
  customers: {
    total: number;
    verified: number;
    active: number;
    suspended: number;
    trend: "UNAVAILABLE";
    trendReason: string;
  };
};

export type DashboardCoverage = {
  basis: "SNAPSHOT_BACKED_ONLY";
  includedTransactions: number;
  excludedTransactions: number;
};

type ApiEnvelope<T> = {
  status: boolean;
  message: string;
  data: T;
};

const EndPoints = {
  getMerchant: `/merchant`,
  allUsers(filterData: { to: string; from: string }) {
    let query = `/merchant/summary/customers`;
    if (filterData?.from) {
      query += `?from=${filterData?.from}`;
    }
    if (filterData?.to) {
      query += `&to=${filterData?.to}`;
    }
    return query;
  },
  verifiedUsers(filterData: { to: string; from: string }) {
    let query = `/merchant/summary/customers?kyc=verified`;
    if (filterData?.from) {
      query += `&from=${filterData?.from}`;
    }
    if (filterData?.to) {
      query += `&to=${filterData?.to}`;
    }
    return query;
  },
  unverifiedUsers(filterData: { to: string; from: string }) {
    let query = `/merchant/summary/customers?kyc=unverified`;
    if (filterData?.from) {
      query += `&from=${filterData?.from}`;
    }
    if (filterData?.to) {
      query += `&to=${filterData?.to}`;
    }
    return query;
  },
  activeUsers(filterData: { to: string; from: string }) {
    let query = `/merchant/summary/customers?isActive=true`;
    if (filterData?.from) {
      query += `&from=${filterData?.from}`;
    }
    if (filterData?.to) {
      query += `&to=${filterData?.to}`;
    }
    return query;
  },
  blockedUsers(filterData: { to: string; from: string }) {
    let query = `/merchant/summary/customers?status=suspended`;
    if (filterData?.from) {
      query += `&from=${filterData?.from}`;
    }
    if (filterData?.to) {
      query += `&to=${filterData?.to}`;
    }
    return query;
  },
  totalTransaction(filterData?: {
    to?: string;
    from?: string;
    currency?: string;
    type?: string;
  }) {
    let query = `/merchant/summary/transactions?math=sum`;
    if (filterData?.currency) {
      query += `&currency=${filterData?.currency}`;
    }
    if (filterData?.type) {
      query += `&type=${filterData?.type}`;
    }
    if (filterData?.from) {
      query += `&from=${filterData?.from}`;
    }
    if (filterData?.to) {
      query += `&to=${filterData?.to}`;
    }
    return query;
  },
  totalTransactionCount(filterData: {
    to: string;
    from: string;
    type?: string;
  }) {
    let query = `/merchant/summary/transactions?math=count`;

    if (filterData?.type) {
      query += `&type=${filterData?.type}`;
    }
    if (filterData?.from) {
      query += `&from=${filterData?.from}`;
    }
    if (filterData?.to) {
      query += `&to=${filterData?.to}`;
    }
    return query;
  },
  transactionRate(filterData: {
    to: string;
    from: string;

    type?: string;
  }) {
    let query = `/merchant/summary/transactions?math=average&column=rate`;

    if (filterData?.type) {
      query += `&type=${filterData?.type}`;
    }
    if (filterData?.from) {
      query += `&from=${filterData?.from}`;
    }
    if (filterData?.to) {
      query += `&to=${filterData?.to}`;
    }
    return query;
  },
  allBalance: `/merchant/balances`,
  availableBalance: `/merchant/balances/available`,

  currency: `/merchant/currency`,
  crypto(currency: string, network: string) {
    return `/merchant/pay-in?currency=${currency}&network=${network}`;
  },
  payout: `/merchant/pay-out`,
  fiat(currency: string) {
    return `/merchant/pay-in?currency=${currency}&network=bep20`;
  },
  bank: `/misc/banks`,
  bankResolve: `/misc/banks/resolve`,
};

export const getDashboardSummary = async (
  period: DashboardPeriod,
): Promise<DashboardSummary> => {
  const response = await api.get<ApiEnvelope<DashboardSummary>>(
    "/merchant/summary/dashboard",
    { params: { period } },
  );
  return response.data.data;
};

export const getMerchant = async () => {
  const res = await api.get(EndPoints.getMerchant);
  return res;
};

export const getAllUserCount = async (filterData: {
  to: string;
  from: string;
}) => {
  const res = await api.get(EndPoints.allUsers(filterData));
  return res;
};

export const getAllVerifiedUsers = async (filterData: {
  to: string;
  from: string;
}) => {
  const res = await api.get(EndPoints.verifiedUsers(filterData));
  return res;
};
export const getAllUnverifiedUsers = async (filterData: {
  to: string;
  from: string;
}) => {
  const res = await api.get(EndPoints.unverifiedUsers(filterData));
  return res;
};
export const getAllActiveUsers = async (filterData: {
  to: string;
  from: string;
}) => {
  const res = await api.get(EndPoints.activeUsers(filterData));
  return res;
};
export const getAllBlockedUsers = async (filterData: {
  to: string;
  from: string;
}) => {
  const res = await api.get(EndPoints.blockedUsers(filterData));
  return res;
};

export const getTotalTransactions = async (filterData?: {
  to?: string;
  from?: string;
}) => {
  const res = await api.get(EndPoints.totalTransaction(filterData));
  return res;
};
export const getTotalTransactionsCount = async (filterData: {
  to: string;
  from: string;
}) => {
  const res = await api.get(EndPoints.totalTransactionCount(filterData));
  return res;
};
export const getTransactionsRate = async (filterData: {
  to: string;
  from: string;
}) => {
  const res = await api.get(EndPoints.transactionRate(filterData));
  return res;
};
export const getAllBalances = async () => {
  const res = await api.get(EndPoints.allBalance);
  return res;
};
export const getAvailableBalances = async () => {
  const res = await api.get(EndPoints.availableBalance);
  return res;
};

export const getAllCurrencyList = async () => {
  const res = await api.get(EndPoints.currency);
  return res;
};

export const getCryptoPayInAddress = async (
  currency: string,
  network: string,
) => {
  const res = await api.get(EndPoints.crypto(currency, network));
  return res;
};
export const getFiatPayIn = async (currency: string) => {
  const res = await api.get(EndPoints.fiat(currency));
  return res;
};
export const getAllBank = async () => {
  const res = await api.get(EndPoints.bank);
  return res;
};
export const getAccountName = async (payload: unknown) => {
  const res = await api.post(EndPoints.bankResolve, payload);
  return res;
};
export const payoutCrypto = async (payload: unknown) => {
  const res = await api.post(EndPoints.payout, payload);
  return res;
};
export const payoutFiat = async (payload: unknown) => {
  const res = await api.post(EndPoints.payout, payload);
  return res;
};
